import PDFKit from 'pdfkit';
import { Ref } from 'vue';
import { format as formatCurrency } from '@/lib/currency';

// Disable param reassign because working with doc becomes horrific otherwise
/* eslint no-param-reassign: 0 */

export const PAGE_WIDTH = 595.28;
export const PAGE_HEIGHT = 841.89;
export const PAGE_MARGINS = 72;
export const CONTENT_WIDTH = PAGE_WIDTH - (2 * PAGE_MARGINS);
export const COL4_WIDTH = CONTENT_WIDTH / 4;
export const MIN_PAGE_SPACE = 20;
export const FONT = 'Helvetica';
export const FONT_BOLD = 'Helvetica-Bold';
export const SIZE_KEY = 11;
export const SIZE_DESCRIPTION = 9;
export const GAP_NORMAL = 1;
export const GAP_DESCRIPTION = 3;
export const COLOR_LIGHT = '#ddd';
export const COLOR_DARK = '#333';
export const COLOR_FONT = 'black';

/**
 * Calculates X-position for a given column index in the 3 column layout used in the table
 *
 * @param column
 */
export function col3(column: number) {
  switch (column) {
    case 0:
      return PAGE_MARGINS;
    case 1:
      return PAGE_MARGINS + CONTENT_WIDTH / 5;
    case 2:
      return PAGE_MARGINS + CONTENT_WIDTH / 2;
    default:
      throw new Error('Invalid column');
  }
}

/**
 * Calculates the X-position fo ra given column in the 4 column layout
 *
 * @param column
 */
export function col4(column: number) {
  return column * COL4_WIDTH + PAGE_MARGINS;
}

/**
 * Checks if the given object is a Vue3 (computed) reference
 *
 * @param obj
 */
function isRef(obj: unknown): obj is Ref {
  return obj instanceof Object
    && 'value' in obj
    // eslint-disable-next-line
    && (obj as any).value !== undefined;
}

/**
 * Unwrap a possibly computed vue reference
 * Returns itself if not a reference
 *
 * @param maybeRef
 */
export function deref<T>(maybeRef: T | Ref<T>): T {
  return isRef(maybeRef) ? maybeRef.value : maybeRef;
}

interface HLineOptions {
  /**
   * Optional override for the starting X position
   */
  x?: number;
  /**
   * Optional override for the color
   */
  color?: string;
  /**
   * Optional vertical padding
   */
  padding?: number;
}

/**
 * Draws a horizontal line on the page
 *
 * After this function, the cursor is moved slightly underneath the line.
 * The X position is unchanged
 *
 * @param doc Current document
 * @param options Options to use
 */
export function hline(doc: PDFKit.PDFDocument, options: HLineOptions = {}) {
  const padding = options.padding ?? 0;
  doc
    .save()
    .strokeColor(options.color ?? COLOR_DARK)
    .moveTo(options.x ?? PAGE_MARGINS, doc.y + padding)
    .lineTo(PAGE_WIDTH - PAGE_MARGINS, doc.y + padding)
    .stroke()
    .restore();
  doc.y += 2 * padding;
}

interface ColorKeyBlockOptions {
  /**
   * Optional override for the size of the square used
   */
  squareSize?: number;
  /**
   * Optional override for the font size used
   */
  fontSize?: number;
  /**
   * Optional maximum width for the entire block. Only applied to the text.
   */
  width?: number;
}

/**
 * Draws a 'color key' legend block, used in both the graphs and the table.
 *
 * After this function, the cursor will be positioned top-left underneath the drawn key.
 *
 * @param doc Current Document
 * @param color Color to describe
 * @param label Name or description of the color
 * @param value The total value (in euros) of the color. Not rendered if null.
 * @param options Additional options
 */
export function colorKeyBlock(
  doc: PDFKit.PDFDocument,
  color: string,
  label: string,
  value: number | null,
  options: ColorKeyBlockOptions = {},
) {
  const squareSize = options.squareSize ?? 40;
  const fontSize = options.fontSize ?? 10;

  if (doc.y >= PAGE_HEIGHT - PAGE_MARGINS - squareSize - MIN_PAGE_SPACE) {
    hline(doc, { color: COLOR_LIGHT });
    doc.addPage();
  }

  doc.save();

  const { x, y } = doc;
  doc
    .fillColor(color)
    .roundedRect(x, y, squareSize, squareSize, 5)
    .fill();

  const valueText = value !== null ? formatCurrency(value) : '';
  const textOptions = {
    width: options.width ? options.width - squareSize - 10 : undefined,
  };

  doc
    .font(FONT)
    .lineGap(0)
    .fillColor('black')
    .fontSize(fontSize)
    .text(label, x + squareSize + 10, y + 5, textOptions)
    .text(valueText, textOptions);

  doc
    .restore()
    .lineGap(2)
    .fontSize(SIZE_DESCRIPTION);
  doc.x = x;
  doc.y = y + squareSize + 10;
}
